import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { ApprovalStatus, OfferStatus, RoleInOffer } from '../constants'
import { allOfferGroupPolicies } from '../constants/offerEditRights'
import { offerActionGroupPolicyType } from '../constants/offerEditRights/offerActionEditRights'
import { PRApprovalType } from '@eapproval/offer-shared'

export const isOtherContributorDelegatee = (contributorDetail, user) => {
  const delegationDetail = contributorDetail?.delegationDetail
  const delegateeId = delegationDetail?.delegatee?._id ?? delegationDetail?.delegatee
  return contributorDetail?.group === ContributorGroupType.OTHER && !!delegateeId && delegateeId === user?._id
}

const getRoleInOffer = (roles, offer) => {
  let roleOrder = [
    RoleInOffer.INITIATOR,
    RoleInOffer.CREATOR,
    RoleInOffer.PROCUREMENT_OFFICER,
    RoleInOffer.CONTRIBUTOR_LEGAL,
    RoleInOffer.CONTRIBUTOR_QS,
    RoleInOffer.CONTRIBUTOR_OTHER,
    RoleInOffer.OPTIONAL_CONTRIBUTOR,
    RoleInOffer.TECHNICAL_PROJECT_MANAGER,
    RoleInOffer.COMMERCIAL_PROJECT_MANAGER,
    RoleInOffer.QUOTATION_APPROVER_DELEGATEE,
    RoleInOffer.QUOTATION_APPROVER,
    RoleInOffer.QUOTATION_APPROVER_INVITEE,
    RoleInOffer.BOOKING_APPROVER_DELEGATEE,
    RoleInOffer.BOOKING_APPROVER,
    RoleInOffer.BOOKING_COMMERCIAL_PROJECT_MANAGER,
    RoleInOffer.BOOKING_APPROVER_INVITEE,
    RoleInOffer.GCPR_APPROVER_INVITEE,
    RoleInOffer.TEAM_ASSISTANT,
    RoleInOffer.ENGINEER,
    RoleInOffer.MANAGER,
    RoleInOffer.BOOKING_MANAGER,

    RoleInOffer.PR_APPROVER_DELEGATEE,
    RoleInOffer.PR_APPROVER,
    RoleInOffer.DEPT_USER,
  ]

  if (
    [OfferStatus.QUOTATION_REVIEWED, OfferStatus.QUOTATION_APPROVED, OfferStatus.QUOTATION_COMPLETED].includes(
      offer.status
    )
  ) {
    //Approver > Contributor Role
    roleOrder = [
      RoleInOffer.INITIATOR,
      RoleInOffer.CREATOR,
      RoleInOffer.PROCUREMENT_OFFICER,
      RoleInOffer.QUOTATION_APPROVER_DELEGATEE,
      RoleInOffer.QUOTATION_APPROVER,
      RoleInOffer.QUOTATION_APPROVER_INVITEE,
      RoleInOffer.CONTRIBUTOR_LEGAL,
      RoleInOffer.CONTRIBUTOR_QS,
      RoleInOffer.CONTRIBUTOR_OTHER,
      RoleInOffer.OPTIONAL_CONTRIBUTOR,
      RoleInOffer.TECHNICAL_PROJECT_MANAGER,
      RoleInOffer.COMMERCIAL_PROJECT_MANAGER,
      RoleInOffer.DEPT_USER,
      RoleInOffer.BOOKING_APPROVER,
      RoleInOffer.BOOKING_COMMERCIAL_PROJECT_MANAGER,

      RoleInOffer.BOOKING_APPROVER_INVITEE,

      RoleInOffer.TEAM_ASSISTANT,
      RoleInOffer.ENGINEER,
      RoleInOffer.MANAGER,
      RoleInOffer.BOOKING_MANAGER,
      RoleInOffer.PR_APPROVER_DELEGATEE,
      RoleInOffer.PR_APPROVER,
      RoleInOffer.GCPR_APPROVER_INVITEE,
      RoleInOffer.GCPR_APPROVER_DELEGATEE,
      RoleInOffer.GCPR_APPROVER,
    ]
  } else if (
    [
      OfferStatus.BOOKING_SUBMITTED,
      OfferStatus.BOOKING_DRAFT,
      OfferStatus.BOOKING_COMPLETED,
      OfferStatus.BOOKING_REJECTED,
    ].includes(offer.status)
  ) {
    //Booking > Quotation Approver > Contributor
    roleOrder = [
      RoleInOffer.INITIATOR,
      RoleInOffer.CREATOR,
      RoleInOffer.PROCUREMENT_OFFICER,
      RoleInOffer.BOOKING_APPROVER_DELEGATEE,
      RoleInOffer.BOOKING_APPROVER,
      RoleInOffer.BOOKING_COMMERCIAL_PROJECT_MANAGER,

      RoleInOffer.BOOKING_APPROVER_INVITEE,

      RoleInOffer.TEAM_ASSISTANT,
      RoleInOffer.ENGINEER,
      RoleInOffer.BOOKING_MANAGER,
      RoleInOffer.MANAGER,

      RoleInOffer.QUOTATION_APPROVER,

      RoleInOffer.QUOTATION_APPROVER_INVITEE,
      RoleInOffer.QUOTATION_APPROVER_DELEGATEE,

      RoleInOffer.CONTRIBUTOR_LEGAL,
      RoleInOffer.CONTRIBUTOR_QS,
      RoleInOffer.CONTRIBUTOR_OTHER,
      RoleInOffer.OPTIONAL_CONTRIBUTOR,
      RoleInOffer.TECHNICAL_PROJECT_MANAGER,
      RoleInOffer.COMMERCIAL_PROJECT_MANAGER,
      RoleInOffer.PR_APPROVER_DELEGATEE,
      RoleInOffer.PR_APPROVER,
      RoleInOffer.GCPR_APPROVER_INVITEE,
      RoleInOffer.GCPR_APPROVER_DELEGATEE,
      RoleInOffer.GCPR_APPROVER,

      RoleInOffer.DEPT_USER,
    ]
  }

  const sortedRoles = roles.sort((a, b) => roleOrder.indexOf(a) - roleOrder.indexOf(b))

  //Special Handle Booking Role

  return sortedRoles.at(0)
}

const getAllRolesInOffer = (user, offer) => {
  const roles = []
  if (offer.createdBy === user._id) {
    roles.push(RoleInOffer.INITIATOR)
  }

  if (offer.creator === user._id) {
    roles.push(RoleInOffer.CREATOR)
  }

  if (user.departments.includes(offer.department)) {
    roles.push(RoleInOffer.DEPT_USER)
  }

  if (offer?.manager && offer.manager.includes(user._id)) {
    roles.push(RoleInOffer.MANAGER)
  }

  const bookingPMId = offer?.booking?.projectManager?._id ?? offer?.booking?.projectManager
  if (bookingPMId && bookingPMId === user._id) {
    roles.push(RoleInOffer.BOOKING_MANAGER)
  }

  if (user?.contributorGroup === ContributorGroupType.PROCUREMENT_OFFICER) {
    roles.push(RoleInOffer.PROCUREMENT_OFFICER)
  }
  //Contributor

  if (Array.isArray(offer?.contributors)) {
    const contributorDetail = offer.contributors.find((contributorDetail) => {
      if ([ContributorGroupType.LEGAL, ContributorGroupType.QS].includes(user.contributorGroup)) {
        return contributorDetail.group === user.contributorGroup
      }

      return contributorDetail.user && contributorDetail.user._id === user._id
    })

    if (contributorDetail) {
      switch (contributorDetail.group) {
        case ContributorGroupType.LEGAL:
          roles.push(RoleInOffer.CONTRIBUTOR_LEGAL)
          break
        case ContributorGroupType.QS:
          roles.push(RoleInOffer.CONTRIBUTOR_QS)
          break
        case ContributorGroupType.OTHER:
          roles.push(RoleInOffer.CONTRIBUTOR_OTHER)
          break
        case ContributorGroupType.TECHNICAL_PROJECT_MANAGER:
          roles.push(RoleInOffer.TECHNICAL_PROJECT_MANAGER)
          break
        case ContributorGroupType.COMMERCIAL_PROJECT_MANAGER:
          roles.push(RoleInOffer.COMMERCIAL_PROJECT_MANAGER)
          break
        default:
          roles.push(RoleInOffer.CONTRIBUTOR_OTHER)
      }
    } else if (offer.contributors.some((detail) => isOtherContributorDelegatee(detail, user))) {
      roles.push(RoleInOffer.CONTRIBUTOR_OTHER)
    }
  }

  if (
    Array.isArray(offer?.optionalContributors) &&
    offer.optionalContributors.some((detail) => detail?.user?._id === user?._id)
  ) {
    roles.push(RoleInOffer.OPTIONAL_CONTRIBUTOR)
  }

  //Quotation Approver

  if (Array.isArray(offer?.approvers) && offer.approvers.some((detail) => detail?.user?._id === user?._id)) {
    roles.push(RoleInOffer.QUOTATION_APPROVER)
  }

  if (
    Array.isArray(offer?.approvers) &&
    offer.approvers.some((detail) => detail?.delegationDetail?.delegatee === user?._id)
  ) {
    roles.push(RoleInOffer.QUOTATION_APPROVER_DELEGATEE)
  }

  if (
    Array.isArray(offer?.approvers) &&
    offer.approvers.some(
      (detail) =>
        Array.isArray(detail?.invitedUsers) && detail?.invitedUsers.some((invitee) => invitee.user._id === user._id)
    )
  ) {
    roles.push(RoleInOffer.QUOTATION_APPROVER_INVITEE)
  }

  //Booking
  if (offer?.engineers && offer.engineers.includes(user._id)) {
    roles.push(RoleInOffer.ENGINEER)
  }

  if (offer?.booking?.teamAssistant.includes(user._id)) {
    roles.push(RoleInOffer.TEAM_ASSISTANT)
  }

  if (
    Array.isArray(offer?.booking?.approverDetails) &&
    offer.booking.approverDetails.some(
      (detail) => detail?.user?._id === user?._id && detail.type !== ContributorGroupType.COMMERCIAL_PROJECT_MANAGER
    )
  ) {
    roles.push(RoleInOffer.BOOKING_APPROVER)
  }

  if (
    Array.isArray(offer?.booking?.approverDetails) &&
    offer.booking.approverDetails.some(
      (detail) => detail?.user?._id === user?._id && detail.type === ContributorGroupType.COMMERCIAL_PROJECT_MANAGER
    )
  ) {
    roles.push(RoleInOffer.BOOKING_COMMERCIAL_PROJECT_MANAGER)
  }

  if (
    Array.isArray(offer?.booking?.approverDetails) &&
    offer.booking.approverDetails.some((detail) => detail?.delegationDetail?.delegatee === user?._id)
  ) {
    roles.push(RoleInOffer.BOOKING_APPROVER_DELEGATEE)
  }

  if (
    Array.isArray(offer?.booking?.approverDetails) &&
    offer.booking.approverDetails.some(
      (detail) =>
        Array.isArray(detail?.invitedUsers) && detail?.invitedUsers.some((invitee) => invitee.user._id === user._id)
    )
  ) {
    roles.push(RoleInOffer.BOOKING_APPROVER_INVITEE)
  }

  //Sourcing PR
  if (Array.isArray(offer?.sourcing?.costBudgets)) {
    offer?.sourcing?.costBudgets.forEach((costBudget) => {
      if (Array.isArray(costBudget?.PR?.vendorSelections)) {
        costBudget.PR.vendorSelections.forEach((vendorSelection) => {
          if (Array.isArray(vendorSelection?.submissions)) {
            vendorSelection.submissions.forEach((submission) => {
              if (Array.isArray(submission?.approverDetails)) {
                submission.approverDetails.forEach((approverDetail) => {
                  if (
                    approverDetail?.type === PRApprovalType.LOGISTIC &&
                    user.contributorGroup === ContributorGroupType.LOGISTIC
                  ) {
                    roles.push(RoleInOffer.PR_APPROVER)
                  } else if (
                    approverDetail?.type === PRApprovalType.COMMERCIAL_PROJECT_MANAGER &&
                    user.contributorGroup === ContributorGroupType.COMMERCIAL_PROJECT_MANAGER
                  ) {
                    roles.push(RoleInOffer.PR_APPROVER)
                  } else if (approverDetail.user === user._id) {
                    roles.push(RoleInOffer.PR_APPROVER)
                  } else if (approverDetail?.delegationDetail?.delegatee === user._id) {
                    roles.push(RoleInOffer.PR_APPROVER_DELEGATEE)
                  }
                })
              }
            })
          }
        })
      }
    })
  }

  //GCPR

  if (Array.isArray(offer?.sourcing?.GCMaterialSubmissions)) {
    offer.sourcing.GCMaterialSubmissions.forEach((GCPRSubmission) => {
      if (Array.isArray(GCPRSubmission?.approverDetails) && !roles.includes(RoleInOffer.GCPR_APPROVER)) {
        GCPRSubmission.approverDetails.forEach((approverDetail) => {
          if (
            approverDetail?.type === PRApprovalType.LICENSE_MANAGER &&
            user.contributorGroup === ContributorGroupType.LICENSE_MANAGER
          ) {
            roles.push(RoleInOffer.GCPR_APPROVER)
          } else if (
            approverDetail?.type === PRApprovalType.LOGISTIC &&
            user.contributorGroup === ContributorGroupType.LOGISTIC
          ) {
            roles.push(RoleInOffer.GCPR_APPROVER)
          } else if (
            approverDetail?.type === PRApprovalType.COMMERCIAL_PROJECT_MANAGER &&
            user.contributorGroup === ContributorGroupType.COMMERCIAL_PROJECT_MANAGER
          ) {
            roles.push(RoleInOffer.GCPR_APPROVER)
          } else if (approverDetail.user === user._id || approverDetail?.user?._id === user._id) {
            roles.push(RoleInOffer.GCPR_APPROVER)
          }
        })
      }

      if (
        Array.isArray(GCPRSubmission?.approverDetails) &&
        !roles.includes(RoleInOffer.GCPR_APPROVER_DELEGATEE) &&
        GCPRSubmission.approverDetails.some(
          (approverDetail) =>
            approverDetail?.delegationDetail?.delegatee === user._id ||
            approverDetail?.delegationDetail?.delegatee?._id === user._id
        )
      ) {
        roles.push(RoleInOffer.GCPR_APPROVER_DELEGATEE)
      }

      if (
        GCPRSubmission.approverDetails.some(
          (approverDetail) =>
            Array.isArray(approverDetail?.invitedUsers) &&
            approverDetail.invitedUsers.some((invitee) => invitee?.user?._id === user?._id)
        )
      ) {
        roles.push(RoleInOffer.GCPR_APPROVER_INVITEE)
      }
    })
  }
  return roles
}

const getRoleDetail = (role, offer, user) => {
  switch (role) {
    case RoleInOffer.CONTRIBUTOR_LEGAL:
    case RoleInOffer.CONTRIBUTOR_QS:
      if (!offer?.contributors) {
        return null
      }
      return offer.contributors.find((detail) => detail.group === user.contributorGroup)
    case RoleInOffer.CONTRIBUTOR_OTHER:
    case RoleInOffer.TECHNICAL_PROJECT_MANAGER:
    case RoleInOffer.COMMERCIAL_PROJECT_MANAGER:
      if (!offer?.contributors) {
        return null
      }
      return (
        offer.contributors.find((detail) => detail?.user?._id === user?._id) ??
        offer.contributors.find((detail) => isOtherContributorDelegatee(detail, user))
      )
    case RoleInOffer.OPTIONAL_CONTRIBUTOR:
      if (!offer?.optionalContributors) {
        return null
      }
      return offer.optionalContributors.find((detail) => detail?.user?._id === user?._id)
    case RoleInOffer.QUOTATION_APPROVER: {
      const approverDetails = offer.approvers.filter((detail) => {
        return detail?.user?._id === user?._id
      })

      let approverDetail
      if (approverDetails.length > 1) {
        approverDetail = approverDetails.find((detail) => {
          return detail.status !== ApprovalStatus.APPROVED
        })
      }

      if (!approverDetail && approverDetails.length > 0) {
        approverDetail = approverDetails[0]
      }
      return approverDetail
    }
    case RoleInOffer.QUOTATION_APPROVER_DELEGATEE: {
      const delegatedApproverDetails = offer.approvers.filter(
        (detail) => detail?.delegationDetail?.delegatee === user?._id
      )
      let approverDetail

      if (delegatedApproverDetails.length > 0) {
        approverDetail = delegatedApproverDetails.find((detail) => detail.status === ApprovalStatus.REQUIRED)
      }

      if (!approverDetail && delegatedApproverDetails.length > 0) {
        approverDetail = delegatedApproverDetails[0]
      }
      return approverDetail
    }
    case RoleInOffer.QUOTATION_APPROVER_INVITEE: {
      let inviteeDetail
      offer.approvers.forEach((approverDetail) => {
        approverDetail.invitedUsers.forEach((invitee) => {
          if (invitee?.user?._id === user?._id) {
            inviteeDetail = invitee
            return
          }
        })
      })
      return inviteeDetail
    }
    case RoleInOffer.BOOKING_COMMERCIAL_PROJECT_MANAGER:
    case RoleInOffer.BOOKING_APPROVER: {
      const approverDetails = offer.booking.approverDetails.filter((detail) => {
        return detail?.user?._id === user?._id
      })

      let approverDetail
      if (approverDetails.length > 1) {
        approverDetail = approverDetails.find((detail) => {
          return detail.status !== ApprovalStatus.APPROVED
        })
      }

      if (!approverDetail && approverDetails.length > 0) {
        approverDetail = approverDetails[0]
      }
      return approverDetail
    }
    case RoleInOffer.BOOKING_APPROVER_DELEGATEE: {
      const delegatedApproverDetails = offer.booking.approverDetails.filter(
        (detail) => detail?.delegationDetail?.delegatee === user?._id
      )
      let approverDetail

      if (delegatedApproverDetails.length > 0) {
        approverDetail = delegatedApproverDetails.find((detail) => detail.status === ApprovalStatus.REQUIRED)
      }

      if (!approverDetail && delegatedApproverDetails.length > 0) {
        approverDetail = delegatedApproverDetails[0]
      }
      return approverDetail
    }
    case RoleInOffer.BOOKING_APPROVER_INVITEE: {
      let inviteeDetail
      offer.booking.approverDetails.forEach((approverDetail) => {
        approverDetail.invitedUsers.forEach((invitee) => {
          if (invitee?.user?._id === user?._id) {
            inviteeDetail = invitee
            return
          }
        })
      })
      return inviteeDetail
    }
    case RoleInOffer.GCPR_APPROVER_INVITEE: {
      let inviteeDetail
      offer.sourcing.GCMaterialSubmissions.forEach((submission) => {
        submission.approverDetails.forEach((approverDetail) => {
          approverDetail.invitedUsers.forEach((invitee) => {
            if (invitee?.user?._id === user?._id) {
              inviteeDetail = invitee
              return
            }
          })
        })
      })
      return inviteeDetail
    }
    case RoleInOffer.GCPR_APPROVER: {
      const allIncludedGCPRSubmissions =
        Array.isArray(offer?.sourcing?.GCMaterialSubmissions) &&
        offer.sourcing.GCMaterialSubmissions.filter((GCPRSubmission) => {
          return GCPRSubmission.approverDetails.some(
            (approverDetail) => approverDetail.user === user._id || approverDetail?.user?._id === user?._id
          )
        })

      if (allIncludedGCPRSubmissions.length === 0) {
        return undefined
      }

      const openedGCPRSubmission = allIncludedGCPRSubmissions.find((submission) => true)
      if (openedGCPRSubmission) {
        return openedGCPRSubmission.approverDetails.find(
          (approverDetail) => approverDetail.user === user._id || approverDetail?.user?._id === user?._id
        )
      }
      return
    }

    case RoleInOffer.GCPR_APPROVER_DELEGATEE: {
      const isDelegateeMatch = (approverDetail) =>
        approverDetail?.delegationDetail?.delegatee === user._id ||
        approverDetail?.delegationDetail?.delegatee?._id === user?._id

      const allIncludedGCPRSubmissions =
        Array.isArray(offer?.sourcing?.GCMaterialSubmissions) &&
        offer.sourcing.GCMaterialSubmissions.filter((GCPRSubmission) =>
          GCPRSubmission.approverDetails.some(isDelegateeMatch)
        )

      if (!allIncludedGCPRSubmissions || allIncludedGCPRSubmissions.length === 0) {
        return undefined
      }

      const openedGCPRSubmission = allIncludedGCPRSubmissions.find((submission) => true)
      if (openedGCPRSubmission) {
        return openedGCPRSubmission.approverDetails.find(isDelegateeMatch)
      }
      return
    }

    default:
      return null
  }
}
export const initialState = {
  offer: null,
  allRoles: [],
  editRights: {},
  role: '',
  adminMode: false,
  editingUserId: '',
}

const offerDetailSlice = createSlice({
  name: 'offerDetail',
  initialState,
  reducers: {
    setOffer: (state, action) => {
      state.offer = action.payload
    },
    setEditRights: (state, action) => {
      state.editRights = action.payload
    },
    setRole: (state, action) => {
      state.role = action.payload
    },
    setAllRoles: (state, action) => {
      state.allRoles = action.payload
    },
    setEditingUserId: (state, action) => {
      state.editingUserId = action.payload
    },
    enableAdminMode: (state) => {
      state.adminMode = true
    },
    disableAdminMode: (state) => {
      state.adminMode = false
    },
  },
})

export const { setOffer, setRole, setAllRoles, setEditingUserId, setEditRights, enableAdminMode, disableAdminMode } =
  offerDetailSlice.actions

export const selectOfferDetail = (state) => state.offerDetail

export const setOfferRole = (user, offer) => (dispatch) => {
  const allRoles = getAllRolesInOffer(user, offer)
  const role = getRoleInOffer(allRoles, offer)

  dispatch(setRole(role))
  // dispatch(setRole(RoleInOffer.COMMERCIAL_PROJECT_MANAGER))
  dispatch(setAllRoles(allRoles))
}

export const isOfferFreeze = (offer) => {
  if (!offer?.status) {
    return false
  }
  if (offer.status === OfferStatus.QUOTATION_REVIEWED) {
    return true
  }

  if (offer.status === OfferStatus.BOOKING_SUBMITTED) {
    if (!Array.isArray(offer?.booking?.approverDetails)) return false
    const currentBookingApprovalLevel = Math.max(
      ...offer.booking.approverDetails
        .filter((detail) => detail?.status === ApprovalStatus.REQUIRED)
        .map((detail) => detail?.level)
    )

    return currentBookingApprovalLevel < 4
  }

  return false
}

export const selectOfferPermissions = createSelector(
  (state) => state.offerDetail.offer,
  (state) => state.offerDetail.role,
  (state) => state.offerDetail.allRoles,
  (state) => state.offerDetail.adminMode,
  (state) => state.offerDetail.editingUserId,
  (state) => state.auth.user,
  (offer, role, allRoles, adminMode, editingUserId, user) => {
    const roleDetail = getRoleDetail(role, offer, user)

    const allRoleDetails = allRoles.map((role) => {
      return {
        role: role,
        roleDetail: getRoleDetail(role, offer, user),
      }
    })

    const permission = {}
    for (const [group, policy] of Object.entries(allOfferGroupPolicies)) {
      permission[group] = {}
      for (const [actionName, rule] of Object.entries(policy)) {
        if (editingUserId !== user._id) {
          //Locked

          //Allow Approver Right

          if (
            offerActionGroupPolicyType.ACTION_QUOTATION_APPROVAL === group &&
            [RoleInOffer.QUOTATION_APPROVER, RoleInOffer.QUOTATION_APPROVER_DELEGATEE].includes(role) &&
            typeof rule === 'function'
          ) {
            const isFreeze = isOfferFreeze(offer)
            permission[group][actionName] = rule({
              role: role,
              isFreeze: isFreeze,
              offerStatus: offer?.status,
              allRoles: allRoles,
              roleDetail: roleDetail,
              allRoleDetails,
              userGroup: user.contributorGroup,
              offer: offer,
              user: user,
              // userGroup: ContributorGroupType.COMMERCIAL_PROJECT_MANAGER,
            })
          } else if (
            offerActionGroupPolicyType.ACTION_BOOKING_APPROVAL === group &&
            [RoleInOffer.BOOKING_APPROVER, RoleInOffer.BOOKING_APPROVER_DELEGATEE].includes(role) &&
            typeof rule === 'function'
          ) {
            const isFreeze = isOfferFreeze(offer)
            permission[group][actionName] = rule({
              role: role,
              isFreeze: isFreeze,
              offerStatus: offer?.status,
              allRoles: allRoles,
              allRoleDetails,
              roleDetail: roleDetail,
              userGroup: user.contributorGroup,
              // userGroup: ContributorGroupType.COMMERCIAL_PROJECT_MANAGER,
            })
          } else {
            permission[group] = {
              view: false,
              edit: false,
            }
          }
        } else if (adminMode) {
          permission[group][actionName] = true
        } else if (typeof rule === 'function') {
          const isFreeze = isOfferFreeze(offer)
          permission[group][actionName] = rule({
            role: role,
            isFreeze: isFreeze,
            offerStatus: offer?.status,
            allRoles: allRoles,
            allRoleDetails: allRoleDetails,
            roleDetail: roleDetail,
            userGroup: user.contributorGroup,
            offer: offer,
            user: user,
            // userGroup: ContributorGroupType.COMMERCIAL_PROJECT_MANAGER,
          })
        } else {
          permission[group][actionName] = rule
        }
      }
    }

    return permission
  }
)

export const selectIsOfferFreeze = createSelector(
  (state) => state.offerDetail.offer,
  (offer) => {
    const isFreeze = isOfferFreeze(offer)

    return isFreeze
  }
)
export default offerDetailSlice.reducer
